<template>
  <MediaGallery
    ref="galleryRef"
    title="Photos"
    :items="photos"
    :job-id="jobId"
    media-type="photo"
    item-type-label="Photo"
    card-class="photo-gallery"
    :has-max-size-text="true"
    :gallery-options="galleryOptions"
    :get-thumbnail-url="getThumbnailUrl"
    @delete-success="(id) => emit('delete-success', id)"
  >
    <template #header-extra>
      <span class="font-sm mr-2"><b>Max size per photo:</b> 20MB</span>
    </template>
    <template #upload-button>
      <SeraButton icon="fa-solid fa-images">
        <label class="mb-0 cursor-pointer">
          Upload Photo(s)
          <input
            ref="fileInputRef"
            type="file"
            accept=".jpg, .png, .jpeg"
            multiple
            class="hidden"
            @change="handleUpload"
          />
        </label>
      </SeraButton>
    </template>
    <template #progress-modal>
      <MediaUploadProgressModal
        v-if="uploading"
        media-type="photo"
        :file-count="selectedFiles.length"
        :total-file-size="totalFileSize"
        :progress="progress"
        @hide-modal="handleCancel"
      />
    </template>
    <template
      #viewer-modal="{
        item,
        close,
        canGoPrevious,
        canGoNext,
        goPrevious,
        goNext
      }"
    >
      <PhotoViewerModal
        v-if="item"
        :photo="item"
        :can-go-previous="canGoPrevious"
        :can-go-next="canGoNext"
        :go-previous="goPrevious"
        :go-next="goNext"
        @hide-modal="close"
      />
    </template>
  </MediaGallery>
</template>

<script setup lang="ts">
import moment from "moment";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { EventBus } from "@/lib/event_bus.js";

import SeraButton from "@/components/Button.vue";
import MediaGallery from "@/components/MediaGallery.vue";
import MediaUpload from "@/components/MediaUpload";
import MediaUploadProgressModal from "@/components/MediaUploadProgressModal.vue";
import CONSTANTS from "@/Constants.js";
import { FailedUpload, MediaFile } from "@/typings/media-upload";
import { Photo } from "@/typings/photo";
import PhotoViewerModal from "./PhotoViewerModal.vue";

const { MEGABYTE, PHOTO_MAX_SIZE } = CONSTANTS;

interface UploadLink {
  href: string;
  method?: string;
}

const MAX_PHOTO_COUNT = 20;

const props = withDefaults(
  defineProps<{
    photos: Photo[];
    jobId: number | null;
    uploadLink: UploadLink;
  }>(),
  {
    photos: () => [],
    jobId: null
  }
);

const emit = defineEmits<{
  "delete-success": [photoId: number];
  "upload-success": [];
}>();

// Template refs
const fileInputRef = ref<HTMLInputElement | null>(null);
const galleryRef = ref<InstanceType<typeof MediaGallery> | null>(null);

// Upload state
const uploading = ref(false);
const selectedFiles = ref<MediaFile[]>([]);
const progress = ref(0);

let uploader: MediaUpload | null = null;

// Gallery options for useGalleryActions
const galleryOptions = {
  deleteAction: "deleteJobPhoto",
  visibilityAction: "updateJobPhotoVisibility",
  visibilityParamKey: "photoId",
  thumbnailEventName: "photo-thumbnail-ready"
};

// Computed
const totalFileSize = computed(() => {
  return selectedFiles.value.reduce((total, file) => {
    return total + file.file.size;
  }, 0);
});

function getThumbnailUrl(photo: Photo): string {
  if (!photo || !photo?.file) return "";
  const { file, created_at } = photo;
  let src = file.thumb?.url || file.url;
  // Use full image URL if recently uploaded (thumb may not be ready)
  if (moment().diff(moment(created_at), "minutes") < 2) {
    src = file.url;
  }
  return src;
}

// Methods
function handleUpload(e: Event) {
  const target = e.target as HTMLInputElement;
  if (!target.files) return;

  if (target.files.length > MAX_PHOTO_COUNT) {
    window.notify.error(
      `You can only upload a max of ${MAX_PHOTO_COUNT} photos at one time. Try splitting photos into multiple uploads.`
    );
    return;
  }

  const data: MediaFile[] = [];
  const fileCount = target.files.length;

  const handleLoad = (file: File) => {
    data.push({
      file: file,
      fileName: file.name
    });
    if (data.length === fileCount) {
      uploadPhotos(data);
    }
  };

  for (let i = 0; i < target.files.length; i++) {
    const file = target.files[i];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => handleLoad(file);
  }
}

async function uploadPhotos(files: MediaFile[]) {
  uploading.value = true;
  selectedFiles.value = files;
  uploader = new MediaUpload({
    mediaType: "photo",
    link: props.uploadLink,
    files
  });
  uploader.onProgress = ({ progress: prog }) => {
    progress.value = prog * 100;
  };
  await uploader.uploadAll();
  handleUploadComplete();
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
}

function formatFileSize(bytes: number): string {
  return `${(bytes / MEGABYTE).toFixed(2)}MB`;
}

function getFailureReason(upload: FailedUpload): string {
  if (upload.reason === "too_large") {
    const maxSizeMB = Math.round(PHOTO_MAX_SIZE / MEGABYTE);
    return `exceeds maximum size of ${maxSizeMB}MB`;
  }
  return "failed to upload";
}

function handleUploadComplete() {
  if (!uploader) return;
  try {
    const { failures, success } = uploader;
    let message = `${success} photo(s) uploaded successfully!`;
    if (failures) {
      message = `${failures} out of ${
        success + failures
      } photo(s) failed to upload.<br/><br/>`;

      uploader.failedUploads.forEach((upload, i) => {
        const fileSize = formatFileSize(upload.file.size);
        const reason = getFailureReason(upload);
        message += `${i + 1}) ${
          upload.fileName
        } (${fileSize}) - ${reason}<br/>`;
      });

      window.notify.error(message);
    } else {
      window.notify.success(message);
    }
    if (document.hidden) {
      alert(message.replace(/<br\/>/gm, "\n"));
    }
  } catch {
    // no-op
  }
  resetUploader();
}

function handleCancel() {
  if (uploader) {
    uploader.onProgress = null;
    uploader.cancel();
    window.notify.error(`Cancelled uploading photo(s)`);
  }
  resetUploader();
}

function resetUploader() {
  uploading.value = false;
  selectedFiles.value = [];
  progress.value = 0;
  uploader = null;
  emit("upload-success");
  setTimeout(() => {
    const modal = document.querySelector(".modal") as HTMLElement | null;
    if (modal) {
      modal.style.display = "none";
    }
  }, 750);
}

// Lifecycle
onMounted(() => {
  EventBus.$on("job-photo-upload-complete", handleUploadComplete);
  window.onbeforeunload = (e: BeforeUnloadEvent) => {
    if (uploading.value) {
      e.preventDefault();
      return "Your pending photos will not be uploaded. Are you sure?";
    }
  };
});

onBeforeUnmount(() => {
  EventBus.$off("job-photo-upload-complete", handleUploadComplete);
  window.onbeforeunload = null;
});

// Expose for testing
defineExpose({
  uploading,
  selectedFiles,
  progress,
  totalFileSize,
  get deleteId() {
    return galleryRef.value?.deleteId;
  },
  get deleteConfirm() {
    return galleryRef.value?.deleteConfirm;
  },
  get viewerItem() {
    return galleryRef.value?.viewerItem;
  },
  handleDelete: (id: number) => galleryRef.value?.handleDelete(id),
  handleVisibilityToggle: (item: Photo) =>
    galleryRef.value?.handleVisibilityToggle(item),
  closeDeleteConfirm: () => galleryRef.value?.closeDeleteConfirm(),
  removePhoto: async () => {
    // Handled by MediaGallery
  }
});
</script>
